import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Button,
  FormLabel,
  Text,
  useToast,
} from '@chakra-ui/react'
import { formatDateTime } from 'BrokerTsLib/src/Utils/formatPrice'
import { useCallback, useEffect, useRef } from 'react'

import type { ContractEmailLog } from 'BrokerTsLib/src/dtos'
import { Loading } from '../../components/Loading'
import { useGetContractEmailLogs } from '../../hooks/useWizard'
import { Email, Refresh } from '../../icons'
import { useSendContractContext } from '../SendContractModal'

export const ContractEmailHistory = ({
  paramsId,
  insurerOfferId,
  isAutomaticallySent = true
}: {
  paramsId: string
  insurerOfferId?: number
  /** Whether the email was sent automatically. When true, the user should be warned when history is empty. When false, the user should be informed that no email was sent. */
  isAutomaticallySent?: boolean
}) => {
  const toast = useToast()
  const { data, refetch, isLoading, isFetching } = useGetContractEmailLogs(paramsId, insurerOfferId)
  const refetchTimeout = useRef<NodeJS.Timeout | null>(null)
  const delay = useRef(500)

  const anyEmailSent = (data?.data.length ?? 0) > 0

  const nextRefetch = useCallback(() => {
    delay.current = Math.min(delay.current * 2, 15000)
    return setTimeout(() => {
      if (!anyEmailSent) {
        refetch().then(() => {
          refetchTimeout.current = nextRefetch()
        })
      }
    }, delay.current)
  }, [anyEmailSent, refetch])

  useEffect(() => {
    refetchTimeout.current = nextRefetch()

    return () => {
      if (refetchTimeout.current) clearTimeout(refetchTimeout.current)
    }
  }, [nextRefetch])

  const manualRefetch = () => {
    refetch().then((res) => {
      if (res.data?.data.length === 0) toast({ title: 'E-mail klientovi dosud nebyl odeslán.' })
      else toast({ title: 'Aktualizováno' })
    })
  }

  const { onOpen } = useSendContractContext()

  return (
    <>
      <FormLabel alignItems='center'>
        <Text>Historie odeslaných e-mailů</Text>
        <Button
          ml={2}
          size='sm'
          variant='ghost'
          onClick={() => manualRefetch()}
          isLoading={isLoading}
        >
          <Refresh />
        </Button>
      </FormLabel>

      {anyEmailSent ? (
        data!.data.map((item, index) =>
          item.success ?
            <ContractEmailSentSuccess key={index} item={item} />
            :
            <ContractEmailSentError key={index} item={item} onOpen={onOpen} />
        )
      ) : isLoading || delay.current < 4000 ?
        <Loading />
        :
        <ContractEmailHistoryWarning
          isFetching={isFetching}
          manualRefetch={manualRefetch}
          onOpen={onOpen}
          isAutomaticallySent={isAutomaticallySent}
        />}
    </>
  )
}

const ContractEmailSentSuccess = (
  { item }: { item: ContractEmailLog }
) => {
  return (<Alert status="success">
    {formatDateTime(item.dateCreated)} byl odeslán e-mail na pojistníka na adresu{' '}
    {item.to}.
  </Alert>)
}

const ContractEmailSentError = (
  { item, onOpen }: { item: ContractEmailLog, onOpen: () => void }
) => {
  return (<Alert status="error">
    {formatDateTime(item.dateCreated)} se nepodařilo odeslat e-mail na pojistníka na
    adresu {item.to}.
    <Button
      display={'inline'}
      variant={'link'}
      color={'inherit'}
      ml={1}
      onClick={onOpen}
    >
      Odešlete e-mail znovu.
    </Button>
  </Alert>)
}

const ContractEmailHistoryWarning = ({
  isFetching,
  manualRefetch,
  onOpen,
  isAutomaticallySent
}: {
  isFetching: boolean
  manualRefetch: () => void
  onOpen: () => void
  isAutomaticallySent: boolean
}) => {

  return (<Alert
    status={isAutomaticallySent ? 'warning' : 'info'}
    variant="subtle"
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    textAlign="center"
    p={6}
  >
    <AlertIcon boxSize="40px" />
    <AlertTitle my={4} fontSize="lg">
      {isAutomaticallySent ? 'E-mail klientovi dosud nebyl odeslán.' : 'E-mail jste klientovi dosud neodeslali.'}
    </AlertTitle>
    <AlertDescription sx={{ display: 'flex', gap: 4 }}>
      {isAutomaticallySent && <Button
        onClick={() => manualRefetch()}
        colorScheme='orange'
        variant='outline'
        isLoading={isFetching}
        gap={2}
      >
        <Refresh />
        <span>Zkontrolovat stav odeslání</span>
      </Button>}
      <Button onClick={onOpen} colorScheme={isAutomaticallySent ? 'orange' : 'blue'} gap={2}>
        <Email />
        <span>{isAutomaticallySent ? 'Odešlete e-mail klientovi manuálně' : 'Odešlete e-mail klientovi'}</span>
      </Button>
    </AlertDescription>
  </Alert>)
}